import { useEffect } from 'react'

import request from 'api'
import axios from 'axios'
import { putData, useAppDispatch, useAppSelector } from 'store'

type BackupData = {
  amountSize: number
  deviceInfos?: {
    deviceId: string
    deviceType: string
    modelCode: string
    name: string
    size: number
    imageUrl: string
  }[]
}

type SyncedData = {
  amountSize: number
  cloudContentList?: {
    cid: string
    count: number
    name: string
    size: number
  }[]
}

type ScloudResponse = {
  isOneDriveUser: boolean
  isProfileUser: boolean
  backupData: BackupData
  syncedData: SyncedData
}

type PkiResponse = {
  images: {
    url: string
  }[]
}

export type Scloud = {
  isScloudUser: boolean
  isOnedriveUser: boolean
  backupData: BackupData
  backupDataCount: number
  syncedData: SyncedData
  syncedDataCount: number
}

const scloudMock = {
  isScloudUser: false,
  isOnedriveUser: false,
  backupData: {
    amountSize: 0,
    deviceInfos: []
  },
  backupDataCount: 0,
  syncedData: {
    amountSize: 0,
    cloudContentList: []
  },
  syncedDataCount: 0
} as Scloud

export function useScloud(forceFetch = false) {
  const scloud = useAppSelector((state) => state.data.scloud as Scloud)
  const dispatch = useAppDispatch()

  const fetchScloud = async () => {
    const fetchData = await request.get<ScloudResponse>('/v1/clouds/samsung').then((response) => {
      if (response.status !== 200) {
        throw new Error('Failed to fetch Scloud')
      }
      return response.data
    })

    const hasData = fetchData.backupData.amountSize !== 0 || fetchData.syncedData.amountSize !== 0

    const backupData = fetchData.backupData
    if (hasData && backupData.deviceInfos !== undefined) {
      for await (const deviceInfo of backupData.deviceInfos) {
        const modelCode = deviceInfo.modelCode.substring(0, 7)
        await axios
          .get<PkiResponse>(`${process.env.REACT_APP_PRODUCT_CATEGORY_URL}/${modelCode}.json`)
          .then((response) => {
            if (response.status !== 200) {
              throw new Error('Failed to fetch Pki')
            }
            return response.data
          })
          .then((pkiResponse: PkiResponse) => {
            deviceInfo.imageUrl = pkiResponse.images.at(0)?.url || '/assets/images/icons/mobile.png'
          })
          .catch(() => {
            deviceInfo.imageUrl = '/assets/images/icons/mobile.png'
          })
      }
    }

    const result: Scloud = {
      isOnedriveUser: fetchData.isOneDriveUser,
      isScloudUser: fetchData.isProfileUser,
      backupData: fetchData.backupData,
      backupDataCount: fetchData.backupData.deviceInfos?.length || 0,
      syncedData: fetchData.syncedData,
      syncedDataCount: fetchData.syncedData.cloudContentList?.length || 0
    }

    dispatch(putData({ scloud: result }))
  }

  useEffect(() => {
    if (forceFetch || scloud === undefined) {
      fetchScloud()
    }
  }, [])

  return { scloud: scloud === undefined ? scloudMock : scloud, isInitializing: scloud === undefined, fetchScloud }
}
